import {
  Component,
  Input,
  ChangeDetectionStrategy,
  OnChanges,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  EventEmitter,
  Output,
  ViewChild,
} from '@angular/core';
import { NgSelectComponent } from '@ng-select/ng-select';
import { unionBy } from 'lodash-es';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  CategorySourceTypes,
  DashpivotEvent,
  EventNotifierService,
  EventTypes,
  ListItem,
  StyleVariants,
  TableCellData,
  TableCellValidator,
  TableCellValidatorFactory,
  TableColumn,
} from '@site-mate/dashpivot-shared-library';

import { FormTableCellComponent } from 'app/form-fields/table/edit-cells/table-cell.component';
import { SegmentService } from 'app/segment/segment.service';

@Component({
  templateUrl: './list.component.html',
  styleUrls: ['../table-cell.component.scss', './list.component.scss'],
  selector: 'cc-table-cell-item-list',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormTableListCellComponent implements FormTableCellComponent, OnChanges, OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private hasListContent = false;
  readonly virtualScrollerThreshold = 30;

  currentValue: string;

  @ViewChild(NgSelectComponent) selector: NgSelectComponent;

  @Input() cellData: TableCellData;
  @Input() listContent?: Observable<ListItem[]>;
  @Input() headerColumn: TableColumn;
  @Output() onChange = new EventEmitter();

  validator: TableCellValidator;
  listItems: ListItem[];
  isManualList: boolean;

  readonly styleVariants = StyleVariants;

  constructor(
    private readonly segmentService: SegmentService,
    private readonly changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.validator = TableCellValidatorFactory.getValidator(this.cellData, this.headerColumn);
    this.attachToListContentIfExists();
    this.currentValue = this.cellData.model.value || null;
    this.isManualList =
      this.cellData.model.listSource !== CategorySourceTypes.List &&
      (this.cellData.model.listSource === CategorySourceTypes.Manual ||
        this.headerColumn.listSource === CategorySourceTypes.Manual);
  }

  ngOnChanges(): void {
    this.attachToListContentIfExists();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get hasNoSelection(): boolean {
    return !!(this.listItems && !this.listItems.length && !this.cellData.model.value);
  }

  register() {
    void EventNotifierService.notify(
      new DashpivotEvent(EventTypes.FormTableCellDataEntered),
      this.segmentService,
    );
  }

  handleOnChange(newValue: string) {
    this.cellData.model.value = newValue ?? '';
    this.cellData.model.style = this.getStyleOfItem(newValue);
    this.currentValue = newValue;
    this.onChange.emit();
  }

  private getStyleOfItem(value: string): StyleVariants {
    if (!value) {
      return StyleVariants.None;
    }
    return this.listItems.find((listItem) => listItem.value === value).style;
  }

  clearSelection() {
    this.handleOnChange(null);
    this.selector.close();
  }

  /**
   * Checks if the provided value has a list of contents. If yes, it extracts the values from there.
   */
  private attachToListContentIfExists(): void {
    if (this.listContent?.pipe && !this.hasListContent) {
      this.hasListContent = true;
      this.listContent.pipe(takeUntil(this.destroy$)).subscribe((items: ListItem[]) => {
        let selected: TableCellData[];
        if (this.cellData.model.value) {
          selected = [];
          selected.push({ value: this.cellData.model.value, style: this.cellData.model.style });
        }

        if (!items && !selected) {
          return;
        }

        // Removes duplicates by ID, to play well when the selected value was deleted from the list
        this.listItems = unionBy(selected, items, (item: { value: string }) => item.value);
        this.changeDetectorRef.detectChanges();
      });
    }
  }
}
